import React, { useState } from 'react';
import type { TemplateContext } from '../App';
import { getAuthHeaders, API_URL } from '../services/auth';
import './PublishTemplate.css';

interface ValidationError {
  code: string;
  key: string;
  message: string;
}

interface ValidationWarning {
  code: string;
  key: string;
  message: string;
}

interface ValidationInfo {
  code: string;
  message: string;
}

interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  info: ValidationInfo[];
  summary: {
    totalTags: number;
    knownTags: number;
    unknownTags: number;
    missingRequired: number;
  };
}

interface PublishTemplateProps {
  activeTemplate: TemplateContext | null;
  onPublished: (template: TemplateContext) => void;
}

export function PublishTemplate({ activeTemplate, onPublished }: PublishTemplateProps) {
  const [publishing, setPublishing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState<ValidationResult | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'info' | 'error'; text: string } | null>(null);

  if (!activeTemplate) {
    return (
      <div className="dp-publish">
        <p className="dp-publish__desc">
          No template saved. Save a template first before publishing.
        </p>
      </div>
    );
  }

  const runValidation = async (): Promise<ValidationResult | null> => {
    setValidating(true);
    setMessage(null);
    setValidation(null);

    try {
      const response = await fetch(
        `${API_URL}/templates/${activeTemplate.id}/versions/${activeTemplate.versionNumber}/validate`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success && data.data) {
        const result = data.data as ValidationResult;
        setValidation(result);
        return result;
      } else {
        setMessage({
          type: 'error',
          text: data.error?.message || 'Validation failed. Check API connection.',
        });
        return null;
      }
    } catch {
      setMessage({
        type: 'error',
        text: 'Validation failed. Check API connection.',
      });
      return null;
    } finally {
      setValidating(false);
    }
  };

  const handleValidate = async () => {
    await runValidation();
  };

  const handlePublish = async () => {
    // Run validation first
    const result = await runValidation();
    if (!result) return;

    // If there are errors, block publish
    if (!result.valid) {
      return;
    }

    // Proceed with publish (warnings are allowed)
    setPublishing(true);
    setMessage(null);

    try {
      const response = await fetch(
        `${API_URL}/templates/${activeTemplate.id}/versions/${activeTemplate.versionNumber}/publish`,
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.published) {
        setMessage({
          type: 'success',
          text: `Published! v${activeTemplate.versionNumber} is now live.`,
        });
        onPublished({
          ...activeTemplate,
          versionStatus: 'Published',
        });
      } else if (data.approvalRequired) {
        setMessage({
          type: 'info',
          text: "Submitted for approval. You'll be notified when approved.",
        });
        onPublished({
          ...activeTemplate,
          versionStatus: 'PendingApproval',
        });
      } else if (data.success === false) {
        setMessage({
          type: 'error',
          text: data.error?.message || 'Failed to publish template.',
        });
      } else {
        // Generic success fallback
        setMessage({
          type: 'success',
          text: `Published! v${activeTemplate.versionNumber} is now live.`,
        });
        onPublished({
          ...activeTemplate,
          versionStatus: 'Published',
        });
      }
    } catch {
      setMessage({
        type: 'error',
        text: 'Failed to publish template. Check API connection.',
      });
    } finally {
      setPublishing(false);
    }
  };

  const canPublish = activeTemplate.versionStatus === 'Draft';
  const hasErrors = validation ? !validation.valid : false;
  const hasWarnings = validation ? validation.warnings.length > 0 : false;
  const isValid = validation ? validation.valid && !hasWarnings : false;
  const publishDisabled = publishing || !canPublish || hasErrors;

  return (
    <div className="dp-publish">
      <p className="dp-publish__desc">
        Publishing makes a template version available for document generation.
      </p>

      <div className="dp-publish__template-info">
        <div className="dp-publish__info-row">
          <span className="dp-publish__info-label">Template</span>
          <span className="dp-publish__info-value">{activeTemplate.name}</span>
        </div>
        <div className="dp-publish__info-row">
          <span className="dp-publish__info-label">ID</span>
          <span className="dp-publish__info-value">{activeTemplate.id}</span>
        </div>
        <div className="dp-publish__info-row">
          <span className="dp-publish__info-label">Version</span>
          <span className="dp-publish__info-value">
            <span className={`dp-publish__version-badge dp-publish__version-badge--${activeTemplate.versionStatus.toLowerCase()}`}>
              {activeTemplate.versionStatus} v{activeTemplate.versionNumber}
            </span>
          </span>
        </div>
      </div>

      {!canPublish && !message && (
        <div className="dp-publish__message dp-publish__message--info">
          Only draft versions can be published. Current status: {activeTemplate.versionStatus}.
        </div>
      )}

      {/* Validation Results */}
      {validation && (
        <div className="dp-publish__validation">
          {/* Summary Line */}
          <div className="dp-publish__validation-summary">
            {validation.summary.totalTags} placeholders found, {validation.summary.knownTags} known, {validation.summary.unknownTags} unknown, {validation.summary.missingRequired} required missing
          </div>

          {/* Validation Status */}
          {isValid && (
            <div className="dp-publish__message dp-publish__message--success">
              Validation passed!
            </div>
          )}

          {hasErrors && (
            <div className="dp-publish__message dp-publish__message--error">
              Fix errors before publishing
            </div>
          )}

          {!hasErrors && hasWarnings && (
            <div className="dp-publish__message dp-publish__message--warning">
              Publish with warnings?
            </div>
          )}

          {/* Errors */}
          {validation.errors.length > 0 && (
            <div className="dp-publish__validation-section">
              {validation.errors.map((err, i) => (
                <div key={`err-${i}`} className="dp-publish__validation-item dp-publish__validation-item--error">
                  <span className="dp-publish__validation-icon">&#x2716;</span>
                  <span className="dp-publish__validation-key">{err.key}</span>
                  <span className="dp-publish__validation-msg">{err.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Warnings */}
          {validation.warnings.length > 0 && (
            <div className="dp-publish__validation-section">
              {validation.warnings.map((warn, i) => (
                <div key={`warn-${i}`} className="dp-publish__validation-item dp-publish__validation-item--warning">
                  <span className="dp-publish__validation-icon">&#x26A0;</span>
                  <span className="dp-publish__validation-key">{warn.key}</span>
                  <span className="dp-publish__validation-msg">{warn.message}</span>
                </div>
              ))}
            </div>
          )}

          {/* Info */}
          {validation.info.filter(i => i.code !== 'TAG_SUMMARY').length > 0 && (
            <div className="dp-publish__validation-section">
              {validation.info.filter(i => i.code !== 'TAG_SUMMARY').map((inf, i) => (
                <div key={`info-${i}`} className="dp-publish__validation-item dp-publish__validation-item--info">
                  <span className="dp-publish__validation-icon">&#x2139;</span>
                  <span className="dp-publish__validation-msg">{inf.message}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Buttons */}
      <div className="dp-publish__actions">
        <button
          className="dp-publish__btn dp-publish__btn--secondary"
          onClick={handleValidate}
          disabled={validating || !canPublish}
          title="Validate template placeholders"
        >
          {validating ? 'Validating...' : 'Validate'}
        </button>

        <button
          className="dp-publish__btn"
          onClick={handlePublish}
          disabled={publishDisabled}
          title={
            hasErrors
              ? 'Fix validation errors before publishing'
              : canPublish
                ? `Publish v${activeTemplate.versionNumber}`
                : 'Only draft versions can be published'
          }
        >
          {publishing ? 'Publishing...' : `Publish v${activeTemplate.versionNumber}`}
        </button>
      </div>

      {message && (
        <div className={`dp-publish__message dp-publish__message--${message.type}`}>
          {message.text}
        </div>
      )}
    </div>
  );
}
